import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface Deal {
  id: string;
  affiliate_partner_id: string | null;
  title: string;
  description: string | null;
  discount_code: string | null;
  discount_percentage: number | null;
  discount_amount: number | null;
  currency: string;
  start_date: string;
  expiry_date: string | null;
  deal_url: string | null;
  is_active: boolean;
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  partner?: {
    partner_name: string;
    logo_url: string | null;
    category: string;
  };
}

export const useDeals = () => {
  return useQuery({
    queryKey: ['deals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select(`
          *,
          partner:affiliate_partners(partner_name, logo_url, category)
        `)
        .order('is_featured', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data as Deal[];
    },
  });
};

export const useActiveDeals = () => {
  return useQuery({
    queryKey: ['active-deals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select(`
          *,
          partner:affiliate_partners(partner_name, logo_url, category)
        `)
        .eq('is_active', true)
        .or('expiry_date.is.null,expiry_date.gt.now()')
        .order('is_featured', { ascending: false })
        .order('expiry_date', { ascending: true });

      if (error) throw error;
      return data as Deal[];
    },
  });
};

export const useFeaturedDeals = () => {
  return useQuery({
    queryKey: ['featured-deals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deals')
        .select(`
          *,
          partner:affiliate_partners(partner_name, logo_url, category)
        `)
        .eq('is_active', true)
        .eq('is_featured', true)
        .or('expiry_date.is.null,expiry_date.gt.now()')
        .order('expiry_date', { ascending: true })
        .limit(5);

      if (error) throw error;
      return data as Deal[];
    },
  });
};

export const useCreateDeal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (deal: Omit<Deal, 'id' | 'created_at' | 'updated_at' | 'partner'>) => {
      const { data, error } = await supabase
        .from('deals')
        .insert(deal)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['active-deals'] });
      queryClient.invalidateQueries({ queryKey: ['featured-deals'] });
      toast.success('Deal created successfully');
    },
    onError: (error) => {
      toast.error('Failed to create deal: ' + error.message);
    },
  });
};

export const useUpdateDeal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Deal> & { id: string }) => {
      const { data, error } = await supabase
        .from('deals')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['active-deals'] });
      queryClient.invalidateQueries({ queryKey: ['featured-deals'] });
      toast.success('Deal updated successfully');
    },
    onError: (error) => {
      toast.error('Failed to update deal: ' + error.message);
    },
  });
};

export const useDeleteDeal = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('deals')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] });
      queryClient.invalidateQueries({ queryKey: ['active-deals'] });
      queryClient.invalidateQueries({ queryKey: ['featured-deals'] });
      toast.success('Deal deleted successfully');
    },
    onError: (error) => {
      toast.error('Failed to delete deal: ' + error.message);
    },
  });
};
